import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Skeleton } from "@/components/ui/skeleton";
import { formatINR } from "@/components/ChefCard";
import { Home, ArrowRight, Users } from "lucide-react";

export default function ChefsTable() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const r = await api.get("/chefs-table");
        setEvents(r.data.events);
      } catch {
        /* ignore */
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div className="max-w-7xl mx-auto px-6 py-16" data-testid="chefs-table-page">
      <div className="max-w-2xl">
        <div className="small-caps text-neutral-500 mb-3">At your home · Any city on request</div>
        <h1 className="font-serif-display text-5xl md:text-6xl leading-none">The Chef's Table.</h1>
        <p className="text-neutral-400 mt-4">
          An intimate, curated tasting menu cooked{" "}
          <span className="text-[#E7D8B5]">in your own kitchen</span> and served course by course at your table.
          A specialist chef, a service team, complete setup and cleanup. Rates on request.
        </p>
      </div>

      <div className="dotted-sep my-10" />

      <div className="grid md:grid-cols-2 gap-8 stagger-in" data-testid="chefs-table-grid">
        {loading ? (
          Array.from({ length: 2 }).map((_, i) => (
            <div key={i} className="card-lux">
              <Skeleton className="aspect-[16/9] rounded-none bg-neutral-900" />
              <div className="p-6">
                <Skeleton className="h-8 w-1/2 bg-neutral-800" />
              </div>
            </div>
          ))
        ) : events.length === 0 ? (
          <div className="text-neutral-500 col-span-2 py-16 text-center">
            No upcoming tables announced yet. Check back soon.
          </div>
        ) : (
          events.map((e) => {
            const left = (e.total_seats || 0) - (e.seats_booked || 0);
            return (
              <Link
                to={`/chefs-table/${e.id}`}
                key={e.id}
                data-testid={`chefs-table-card-${e.id}`}
                className="card-lux group overflow-hidden flex flex-col"
              >
                <div className="aspect-[16/9] overflow-hidden bg-neutral-900 relative">
                  <img
                    src={e.image_url}
                    alt={e.title}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  {e.featured && (
                    <span className="absolute top-4 left-4 chip text-[#F5B041] border-[#F5B041]/40 bg-black/60">
                      Signature evening
                    </span>
                  )}
                </div>
                <div className="p-6 flex flex-col flex-1">
                  <div className="flex items-center gap-4 text-xs text-neutral-400 flex-wrap">
                    <span className="flex items-center gap-1">
                      <Home size={12} className="text-[#F5B041]" /> At your home
                    </span>
                    <span className="flex items-center gap-1">
                      <Users size={12} className="text-[#F5B041]" /> Up to {e.total_seats} guests
                    </span>
                  </div>
                  <h3 className="font-serif-display text-3xl mt-3 leading-tight">{e.title}</h3>
                  <p className="font-script text-xl text-[#F5B041] mt-1">{e.tagline}</p>
                  <p className="text-neutral-400 text-sm mt-3 line-clamp-2 flex-1">{e.description}</p>
                  <div className="dotted-sep my-5" />
                  <div className="flex items-end justify-between">
                    <div>
                      <div className="small-caps text-[10px] text-neutral-500">Per guest</div>
                      <div className="font-serif-display text-3xl">
                        {e.price_on_request || !e.cover_charge ? (
                          <span className="text-2xl text-[#E7D8B5]">On request</span>
                        ) : (
                          <>₹{formatINR(e.cover_charge)}<span className="text-sm text-neutral-500"> /guest</span></>
                        )}
                      </div>
                    </div>
                    <div className="text-right">
                      <span className="inline-flex items-center gap-1 small-caps text-[11px] text-[#E7D8B5] group-hover:text-white mt-2">
                        Enquire <ArrowRight size={13} />
                      </span>
                    </div>
                  </div>
                </div>
              </Link>
            );
          })
        )}
      </div>
    </div>
  );
}
